/** Dominio de producción Pinturas: fórmulas, lotes/OP y etapas de inventario. */

import { inventoryQtyFromPurchase } from '../../utils/packFormatQty';
import {
  getActiveProductionCatalogOptions,
  getProductionCatalogColorMap,
} from './productionCatalogDefaults';
import { quantityToKg, unitCostOf } from './rawMaterialsDomain';

export const FORMULA_STATUSES = ['Borrador', 'Activa', 'Obsoleta'];

export const FORMULA_STATUS_COLORS = {
  Borrador: '#64748b',
  Activa: '#16a34a',
  Obsoleta: '#9ca3af',
};

export const FORMULA_KIND_BASE = 'base';
export const FORMULA_KIND_COLOR = 'color';

export const FORMULA_KIND_LABELS = {
  base: 'Bases',
  color: 'Colores',
};

export const formulaKindLabel = (kind) =>
  kind === FORMULA_KIND_COLOR ? 'Color' : 'Base';

export const normalizeFormulaKind = (row) => {
  if (row?.formula_kind === FORMULA_KIND_COLOR || row?.formula_kind === FORMULA_KIND_BASE) {
    return row.formula_kind;
  }
  const hay = `${row?.code || ''} ${row?.product_name || ''} ${row?.color_name || ''} ${row?.notes || ''}`.toLowerCase();
  if (/^fo-c-/.test(String(row?.code || '').toLowerCase())) return FORMULA_KIND_COLOR;
  if (/\b(color|gris|ral|esmalte|símil)\b/.test(hay)) return FORMULA_KIND_COLOR;
  return FORMULA_KIND_BASE;
};

export const BATCH_STATUSES = [
  'Borrador',
  'Planificada',
  'Emitida',
  'En proceso',
  'Fabricado',
  'En calidad',
  'Reproceso',
  'Terminado',
  'Anulada',
];

export const BATCH_STATUS_COLORS = {
  Borrador: '#9ca3af',
  Planificada: '#2563eb',
  Emitida: '#0ea5e9',
  'En proceso': '#d97706',
  Fabricado: '#7c3aed',
  'En calidad': '#c026d3',
  Reproceso: '#c2410c',
  Terminado: '#15803d',
  Anulada: '#dc2626',
};

export const BATCH_ACCOUNTING_EVENTS = {
  Emitida: 'production.issued',
  Fabricado: 'production.manufactured',
  Terminado: 'production.finished',
};

export const BATCH_TAB_STATUSES = {
  all: BATCH_STATUSES,
  plant: ['Emitida', 'En proceso', 'Reproceso'],
  quality: ['Fabricado', 'En calidad'],
  done: ['Terminado', 'Anulada'],
};

/** Estados de lote activos desde Producción → Ajustes. */
export const getActiveBatchStatuses = () => {
  const options = getActiveProductionCatalogOptions('batch_status');
  const labels = options.map((opt) => opt.value).filter(Boolean);
  return labels.length ? labels : BATCH_STATUSES;
};

export const getBatchStatusColorMap = () => ({
  ...BATCH_STATUS_COLORS,
  ...getProductionCatalogColorMap('batch_status'),
});

export const getBatchStatusColor = (status) =>
  getBatchStatusColorMap()[String(status || '').trim()] || '#64748b';

/** Estados de fórmula activos desde Producción → Ajustes. */
export const getActiveFormulaStatuses = () => {
  const options = getActiveProductionCatalogOptions('formula_status');
  const labels = options.map((opt) => opt.value).filter(Boolean);
  return labels.length ? labels : FORMULA_STATUSES;
};

export const getFormulaStatusColorMap = () => ({
  ...FORMULA_STATUS_COLORS,
  ...getProductionCatalogColorMap('formula_status'),
});

export const getFormulaStatusColor = (status) =>
  getFormulaStatusColorMap()[String(status || '').trim()] || '#64748b';

export const PAINT_PRODUCT_OPTIONS = [
  { value: 'A006', label: 'A006 · Pintura epóxica gris T-COAT' },
  { value: 'BW01', label: 'BW01 · Primer blanco T-COAT' },
  { value: 'A012', label: 'A012 · Esmalte acrílico interior' },
  { value: 'BS005', label: 'BS005 · Diluyente epóxico' },
];

export const MATERIAL_CATALOG = [
  { code: 'RS0001', name: 'Resina epóxica', kind: 'Materias primas', unit: 'kg', stock: 1840 },
  { code: 'PG0001', name: 'Dióxido de titanio', kind: 'Materias primas', unit: 'kg', stock: 620 },
  { code: 'PG0002', name: 'Negro de humo furnace', kind: 'Materias primas', unit: 'kg', stock: 24 },
  { code: 'DY0001', name: 'Xileno', kind: 'Materias primas', unit: 'L', stock: 410 },
  { code: 'EX0001', name: 'Carbonato de calcio', kind: 'Materias primas', unit: 'kg', stock: 960 },
  { code: 'AD0001', name: 'Antiespumante siliconado', kind: 'Materias primas', unit: 'kg', stock: 38 },
  { code: 'EN0001', name: 'Tineta metálica 16 L', kind: 'Envases', unit: 'UN', stock: 180 },
  { code: 'EQ0001', name: 'Etiqueta tineta 16 L', kind: 'Etiquetas', unit: 'UN', stock: 2400 },
];

export const WAREHOUSE_OPTIONS = ['Bodega JR 1', 'Bodega Planta 2', 'Bodega MP'];

export const RESPONSIBLE_OPTIONS = ['Planta Pinturas', 'Turno A', 'Turno B', 'Jefe de producción'];

const FORMULAS_KEY = 'sqmt.production.formulas.v5';
const FORMULAS_KEY_LEGACY = 'sqmt.production.formulas.v4';
const BATCHES_KEY = 'sqmt.production.batches.v1';
export const PRODUCTION_CHANGED_EVENT = 'sqmt-production-changed';
export const FORMULA_COST_BASIS_OPTIONS = [
  { value: 'L', label: 'Por litro (L)' },
  { value: 'gl', label: 'Por galón (gl)' },
];
export const FORMULA_COMPONENT_UNITS = ['kg', 'g', 'L', 'mL'];
/** Litros por galón US (mismo criterio comercial T-COAT). */
export const FORMULA_GALLON_LITERS = 3.785;

const todayIso = () => new Date().toISOString().slice(0, 10);

const isoDaysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
};

const eventAt = (status, at, note = '') => ({
  status,
  at,
  note,
  accounting: BATCH_ACCOUNTING_EVENTS[status] || null,
});

const seedFormulaLine = (item_code, item_name, kind, quantity, unit = 'kg', scrap_pct = 0) => ({
  item_code,
  item_name,
  kind,
  quantity,
  unit,
  scrap_pct,
});

const seedFormulas = () => [
  {
    id: 1,
    formula_kind: FORMULA_KIND_BASE,
    code: 'FO-B-001',
    name: 'Base epóxica T-COAT',
    product_code: 'A006',
    product_name: 'Base epóxica T-COAT',
    color_code: '',
    color_name: '',
    base_formula_id: null,
    base_formula_code: '',
    version: 1,
    yield_qty: 100,
    yield_unit: 'L',
    component_unit: 'kg',
    cost_basis: 'L',
    status: 'Activa',
    notes: 'Vehículo epóxico sin pigmentación de color. Se tintea en fórmulas de color.',
    tech_params: [],
    updated_at: isoDaysAgo(4),
    lines: [
      seedFormulaLine('RS0001', 'Resina epóxica', 'Materias primas', 42, 'kg', 1.5),
      seedFormulaLine('DY0001', 'Solvente xileno', 'Materias primas', 12, 'L', 3),
      seedFormulaLine('EX0001', 'Carbonato de calcio', 'Materias primas', 28, 'kg', 0.5),
      seedFormulaLine('AD0001', 'Antiespumante siliconado', 'Materias primas', 0.8, 'kg', 0),
    ],
  },
  {
    id: 2,
    formula_kind: FORMULA_KIND_BASE,
    code: 'FO-B-002',
    name: 'Base primer blanco T-COAT',
    product_code: 'BW01',
    product_name: 'Base primer blanco T-COAT',
    color_code: '',
    color_name: '',
    base_formula_id: null,
    base_formula_code: '',
    version: 2,
    yield_qty: 80,
    yield_unit: 'L',
    component_unit: 'kg',
    cost_basis: 'L',
    status: 'Activa',
    notes: 'Base blanca de primer. Rendimiento tineta 16 L × 5.',
    tech_params: [],
    updated_at: isoDaysAgo(12),
    lines: [
      seedFormulaLine('RS0001', 'Resina epóxica', 'Materias primas', 30, 'kg', 1),
      seedFormulaLine('PG0001', 'Dióxido de titanio', 'Materias primas', 26, 'kg', 1.2),
      seedFormulaLine('DY0001', 'Solvente xileno', 'Materias primas', 8, 'L', 2),
    ],
  },
  {
    id: 3,
    formula_kind: FORMULA_KIND_COLOR,
    code: 'FO-C-001',
    name: 'Epóxica gris RAL 7035',
    product_code: 'A006',
    product_name: 'Pintura epóxica gris T-COAT',
    color_code: 'RAL 7035',
    color_name: 'Gris claro',
    base_formula_id: 1,
    base_formula_code: 'FO-B-001',
    version: 3,
    yield_qty: 100,
    yield_unit: 'L',
    component_unit: 'kg',
    cost_basis: 'L',
    status: 'Activa',
    notes: 'Color sobre base epóxica. Merma estándar 2%.',
    tech_params: [],
    updated_at: isoDaysAgo(4),
    lines: [
      seedFormulaLine('FO-B-001', 'Base epóxica T-COAT', 'Base', 92, 'L', 1),
      seedFormulaLine('PG0001', 'Dióxido de titanio', 'Materias primas', 6.5, 'kg', 1),
      seedFormulaLine('PG0002', 'Negro de humo furnace', 'Materias primas', 0.9, 'kg', 1),
    ],
  },
  {
    id: 4,
    formula_kind: FORMULA_KIND_COLOR,
    code: 'FO-C-002',
    name: 'Epóxica gris RAL 7035 (v2)',
    product_code: 'A006',
    product_name: 'Pintura epóxica gris T-COAT',
    color_code: 'RAL 7035',
    color_name: 'Gris claro',
    base_formula_id: 1,
    base_formula_code: 'FO-B-001',
    version: 2,
    yield_qty: 100,
    yield_unit: 'L',
    component_unit: 'kg',
    cost_basis: 'L',
    status: 'Obsoleta',
    notes: 'Reemplazada por FO-C-001.',
    tech_params: [],
    updated_at: isoDaysAgo(40),
    lines: [
      seedFormulaLine('FO-B-001', 'Base epóxica T-COAT', 'Base', 90, 'L', 1),
      seedFormulaLine('PG0001', 'Dióxido de titanio', 'Materias primas', 8, 'kg', 1),
      seedFormulaLine('DY0001', 'Solvente xileno', 'Materias primas', 4, 'L', 2),
    ],
  },
  {
    id: 5,
    formula_kind: FORMULA_KIND_COLOR,
    code: 'FO-C-003',
    name: 'Esmalte Alabaster SW 7008',
    product_code: 'A012',
    product_name: 'Esmalte acrílico interior',
    color_code: 'SW 7008',
    color_name: 'Alabaster',
    base_formula_id: 2,
    base_formula_code: 'FO-B-002',
    version: 1,
    yield_qty: 100,
    yield_unit: 'L',
    component_unit: 'kg',
    cost_basis: 'L',
    status: 'Borrador',
    notes: 'Pendiente de validación de color en laboratorio.',
    tech_params: [],
    updated_at: isoDaysAgo(1),
    lines: [
      seedFormulaLine('FO-B-002', 'Base primer blanco T-COAT', 'Base', 96, 'L', 1),
      seedFormulaLine('PG0001', 'Dióxido de titanio', 'Materias primas', 3.2, 'kg', 1),
    ],
  },
];

const seedBatches = () => [
  {
    id: 1,
    code: 'OP-2026-014',
    formula_id: 3,
    formula_code: 'FO-C-001',
    request_code: 'SP-2026-008',
    color_code: 'RAL 7035',
    formula_kind: 'color',
    product_code: 'A006',
    product_name: 'Pintura epóxica gris T-COAT',
    planned_qty: 320,
    actual_qty: 0,
    unit: 'L',
    status: 'En proceso',
    planned_date: isoDaysAgo(2),
    warehouse: 'Bodega JR 1',
    responsible: 'Turno A',
    notes: 'Pedido comercial COT-1842.',
    created_at: isoDaysAgo(3),
    events: [
      eventAt('Borrador', isoDaysAgo(3), 'Lote creado'),
      eventAt('Planificada', isoDaysAgo(3), 'Programado turno A'),
      eventAt('Emitida', isoDaysAgo(2), 'MP consumida hacia WIP'),
      eventAt('En proceso', isoDaysAgo(2), 'Molino 2 en marcha'),
    ],
  },
  {
    id: 2,
    code: 'OP-2026-015',
    formula_id: 2,
    formula_code: 'FO-B-002',
    formula_kind: 'base',
    product_code: 'BW01',
    product_name: 'Primer blanco T-COAT',
    planned_qty: 160,
    actual_qty: 158,
    unit: 'L',
    status: 'En calidad',
    planned_date: isoDaysAgo(5),
    warehouse: 'Bodega Planta 2',
    responsible: 'Turno B',
    notes: 'Espera liberación QC viscosidad.',
    created_at: isoDaysAgo(6),
    events: [
      eventAt('Borrador', isoDaysAgo(6)),
      eventAt('Planificada', isoDaysAgo(6)),
      eventAt('Emitida', isoDaysAgo(5), 'MP → WIP'),
      eventAt('En proceso', isoDaysAgo(5)),
      eventAt('Fabricado', isoDaysAgo(4), 'WIP → producto fabricado'),
      eventAt('En calidad', isoDaysAgo(4), 'Muestra enviada a laboratorio'),
    ],
  },
  {
    id: 3,
    code: 'OP-2026-012',
    formula_id: 3,
    formula_code: 'FO-C-001',
    request_code: 'SP-2026-006',
    product_code: 'A006',
    product_name: 'Pintura epóxica gris T-COAT',
    planned_qty: 200,
    actual_qty: 204,
    unit: 'L',
    status: 'Terminado',
    planned_date: isoDaysAgo(18),
    warehouse: 'Bodega JR 1',
    responsible: 'Planta Pinturas',
    notes: '',
    created_at: isoDaysAgo(20),
    events: [
      eventAt('Borrador', isoDaysAgo(20)),
      eventAt('Planificada', isoDaysAgo(19)),
      eventAt('Emitida', isoDaysAgo(18), 'MP → WIP'),
      eventAt('En proceso', isoDaysAgo(18)),
      eventAt('Fabricado', isoDaysAgo(17), 'WIP → fabricado'),
      eventAt('En calidad', isoDaysAgo(17)),
      eventAt('Terminado', isoDaysAgo(16), 'Liberado a PT'),
    ],
  },
  {
    id: 4,
    code: 'OP-2026-016',
    formula_id: 2,
    formula_code: 'FO-B-002',
    request_code: 'SP-2026-009',
    product_code: 'BW01',
    product_name: 'Primer blanco T-COAT',
    planned_qty: 80,
    actual_qty: 0,
    unit: 'L',
    status: 'Planificada',
    planned_date: todayIso(),
    warehouse: 'Bodega MP',
    responsible: 'Jefe de producción',
    notes: 'Reposición stock bajo.',
    created_at: isoDaysAgo(1),
    events: [eventAt('Borrador', isoDaysAgo(1)), eventAt('Planificada', isoDaysAgo(1), 'Calendario planta')],
  },
  {
    id: 5,
    code: 'OP-2026-011',
    formula_id: 3,
    formula_code: 'FO-C-001',
    color_code: 'RAL 7035',
    formula_kind: 'color',
    product_code: 'A006',
    product_name: 'Pintura epóxica gris T-COAT',
    planned_qty: 96,
    actual_qty: 90,
    unit: 'L',
    status: 'Reproceso',
    planned_date: isoDaysAgo(9),
    warehouse: 'Bodega JR 1',
    responsible: 'Turno A',
    notes: 'QC rechazó brillo. Ajuste de molienda.',
    created_at: isoDaysAgo(11),
    events: [
      eventAt('Borrador', isoDaysAgo(11)),
      eventAt('Planificada', isoDaysAgo(10)),
      eventAt('Emitida', isoDaysAgo(9), 'MP → WIP'),
      eventAt('En proceso', isoDaysAgo(9)),
      eventAt('Fabricado', isoDaysAgo(8), 'WIP → fabricado'),
      eventAt('En calidad', isoDaysAgo(8)),
      eventAt('Reproceso', isoDaysAgo(7), 'No conforme: brillo fuera de rango'),
    ],
  },
];

const readJson = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length ? parsed : fallback();
  } catch {
    return fallback();
  }
};

const writeJson = (key, value) => {
  window.localStorage.setItem(key, JSON.stringify(value));
  window.dispatchEvent(new Event(PRODUCTION_CHANGED_EVENT));
};

const normalizeFormulaRow = (row) => {
  const kind = normalizeFormulaKind(row);
  const name =
    String(row?.name || '').trim() ||
    String(row?.product_name || '').trim() ||
    String(row?.code || '').trim();
  const componentUnit = String(row?.component_unit || 'kg').trim() || 'kg';
  const costBasis = String(row?.cost_basis || 'L').trim() === 'gl' ? 'gl' : 'L';
  const lines = (Array.isArray(row?.lines) ? row.lines : []).map((line) => ({
    ...line,
    unit: String(line?.unit || componentUnit || 'kg').trim() || 'kg',
    scrap_pct: Number(line?.scrap_pct || 0) || 0,
  }));
  return {
    ...row,
    formula_kind: kind,
    name,
    product_code: row?.product_code || '',
    product_name: row?.product_name || '',
    color_code: row?.color_code || '',
    color_name: row?.color_name || '',
    base_formula_id: row?.base_formula_id || null,
    base_formula_code: row?.base_formula_code || '',
    component_unit: componentUnit,
    cost_basis: costBasis,
    tech_params: Array.isArray(row?.tech_params) ? row.tech_params : [],
    lines,
  };
};

export const loadFormulas = () => {
  if (typeof window === 'undefined') return seedFormulas().map(normalizeFormulaRow);
  try {
    const raw = window.localStorage.getItem(FORMULAS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return (Array.isArray(parsed) ? parsed : seedFormulas()).map(normalizeFormulaRow);
    }
    const legacy = window.localStorage.getItem(FORMULAS_KEY_LEGACY);
    if (legacy) {
      const parsed = JSON.parse(legacy);
      const migrated = (Array.isArray(parsed) ? parsed : seedFormulas()).map(normalizeFormulaRow);
      writeJson(FORMULAS_KEY, migrated);
      return migrated;
    }
  } catch {
    /* fall through */
  }
  return seedFormulas().map(normalizeFormulaRow);
};
export const loadBatches = () => readJson(BATCHES_KEY, seedBatches);

export const saveFormulas = (rows) => writeJson(FORMULAS_KEY, rows);
export const saveBatches = (rows) => writeJson(BATCHES_KEY, rows);

export const formatQty = (value, unit = '', formatCode = '') => {
  const converted = inventoryQtyFromPurchase(value, unit, formatCode);
  const formatted = Number(converted.qty || 0).toLocaleString('es-CL', { maximumFractionDigits: 2 });
  return converted.unit ? `${formatted} ${converted.unit}` : formatted;
};

export const formatDateEs = (value) => {
  if (!value) return '—';
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('es-CL');
};

export const productLabel = (code) =>
  PAINT_PRODUCT_OPTIONS.find((option) => option.value === code)?.label || code || '—';

export const materialByCode = (code) => MATERIAL_CATALOG.find((row) => row.code === code) || null;

export const nextFormulaCode = (formulas, kind = FORMULA_KIND_BASE) => {
  const tag = kind === FORMULA_KIND_COLOR ? 'C' : 'B';
  const prefix = `FO-${tag}-`;
  const maxSeq = (formulas || [])
    .filter((row) => normalizeFormulaKind(row) === kind)
    .map((row) => {
      const match = String(row.code || '')
        .trim()
        .toUpperCase()
        .match(new RegExp(`^FO-${tag}-(\\d+)`, 'i'));
      return match ? Number(match[1]) : Number(row.version) || 0;
    })
    .filter((n) => Number.isFinite(n))
    .reduce((max, n) => Math.max(max, n), 0);
  const version = maxSeq + 1;
  return { code: `${prefix}${String(version).padStart(3, '0')}`, version };
};

export const emptyFormulaLine = (unit = 'kg') => ({
  item_code: '',
  item_name: '',
  kind: 'Materias primas',
  quantity: '',
  unit: unit || 'kg',
  scrap_pct: 0,
});

/** Masa en kg de una línea (usa densidad del material si la unidad es volumétrica). */
export const formulaLineMassKg = (line, materialByCodeMap = {}) => {
  const material = materialByCodeMap[String(line?.item_code || '').trim()] || null;
  return quantityToKg(line?.quantity, line?.unit || material?.unit || 'kg', material);
};

/** % m/m por línea sobre la masa total de componentes. */
export const formulaMassPercentages = (lines = [], materialByCodeMap = {}) => {
  const masses = (Array.isArray(lines) ? lines : []).map((line) =>
    formulaLineMassKg(line, materialByCodeMap)
  );
  const total = masses.reduce((sum, kg) => sum + (Number.isFinite(kg) ? kg : 0), 0);
  return masses.map((kg) => (total > 0 ? (kg / total) * 100 : 0));
};

/** Rendimiento de la fórmula expresado en litros. */
export const formulaYieldLiters = (formula) => {
  const qty = Number(formula?.yield_qty || 0);
  if (!Number.isFinite(qty) || qty <= 0) return 0;
  const unit = String(formula?.yield_unit || 'L').trim().toLowerCase();
  if (unit === 'l' || unit === 'lt' || unit === 'litro' || unit === 'litros') return qty;
  if (unit === 'gl' || unit === 'gal' || unit === 'galon' || unit === 'galón') {
    return qty * FORMULA_GALLON_LITERS;
  }
  // kg u otras: sin densidad de fórmula, no convertir
  return 0;
};

export const formulaCostDivisor = (formula) => {
  const liters = formulaYieldLiters(formula);
  if (liters <= 0) return 0;
  const basis = String(formula?.cost_basis || 'L').trim() === 'gl' ? 'gl' : 'L';
  return basis === 'gl' ? liters / FORMULA_GALLON_LITERS : liters;
};

export const formulaLineUnitCost = (line, materialByCodeMap = {}, catalogByCode = {}) => {
  const material = materialByCodeMap[String(line?.item_code || '').trim()] || null;
  if (!material) return 0;
  const hasProcurement =
    Boolean(String(material.procurement_code || '').trim()) ||
    Boolean(material.procurement_item_id) ||
    Number(material.unit_cost) > 0 ||
    Number(material.acquisition_unit_cost) > 0;
  if (!hasProcurement && !(Number(material.unit_cost) > 0)) return 0;
  return unitCostOf(material, catalogByCode);
};

/** Costo del componente en el batch de rendimiento, y costo normalizado por L o gl. */
export const formulaLineCostBreakdown = (
  line,
  formula,
  materialByCodeMap = {},
  catalogByCode = {}
) => {
  const unitCost = formulaLineUnitCost(line, materialByCodeMap, catalogByCode);
  const qty = Number(line?.quantity || 0);
  const lineCost = Number.isFinite(qty) && qty > 0 ? qty * unitCost : 0;
  const divisor = formulaCostDivisor(formula);
  const contribution = divisor > 0 ? lineCost / divisor : 0;
  return { unitCost, lineCost, contribution };
};

export const nextBatchCode = (batches) => {
  const year = new Date().getFullYear();
  const prefix = `OP-${year}-`;
  const max = batches
    .map((row) => Number(String(row.code || '').replace(prefix, '')))
    .filter((n) => Number.isFinite(n))
    .reduce((acc, n) => Math.max(acc, n), 0);
  return `${prefix}${String(max + 1).padStart(3, '0')}`;
};

export const scaleFormulaLines = (formula, plannedQty) => {
  const yieldQty = Number(formula?.yield_qty) || 1;
  const factor = Number(plannedQty || 0) / yieldQty;
  return (formula?.lines || []).map((line) => {
    const theoretical = Number(line.quantity || 0) * factor;
    const withScrap = theoretical * (1 + Number(line.scrap_pct || 0) / 100);
    return {
      ...line,
      theoretical_qty: theoretical,
      required_qty: withScrap,
    };
  });
};

export const consumptionGaps = (formula, plannedQty) =>
  scaleFormulaLines(formula, plannedQty).map((line) => {
    const material = materialByCode(line.item_code);
    const stock = Number(material?.stock || 0);
    return {
      ...line,
      stock,
      short: stock < Number(line.required_qty || 0),
    };
  });

export const nextBatchStatuses = (status) => {
  switch (status) {
    case 'Borrador':
      return ['Planificada', 'Anulada'];
    case 'Planificada':
      return ['Emitida', 'Anulada'];
    case 'Emitida':
      return ['En proceso'];
    case 'En proceso':
      return ['Fabricado'];
    case 'Fabricado':
      return ['En calidad'];
    case 'En calidad':
      return ['Terminado', 'Reproceso'];
    case 'Reproceso':
      return ['En proceso'];
    default:
      return [];
  }
};

export const transitionLabel = (status) => {
  switch (status) {
    case 'Planificada':
      return 'Planificar';
    case 'Emitida':
      return 'Emitir (MP → WIP)';
    case 'En proceso':
      return 'Iniciar proceso';
    case 'Fabricado':
      return 'Marcar fabricado';
    case 'En calidad':
      return 'Enviar a calidad';
    case 'Terminado':
      return 'Liberar a PT';
    case 'Reproceso':
      return 'Devolver a reproceso';
    case 'Anulada':
      return 'Anular';
    default:
      return status;
  }
};

export const emptyFormula = (kind = FORMULA_KIND_BASE) => ({
  id: null,
  formula_kind: kind === FORMULA_KIND_COLOR ? FORMULA_KIND_COLOR : FORMULA_KIND_BASE,
  code: '',
  name: '',
  product_code: '',
  product_name: '',
  color_code: '',
  color_name: '',
  base_formula_id: '',
  base_formula_code: '',
  version: 1,
  yield_qty: 100,
  yield_unit: 'L',
  component_unit: 'kg',
  cost_basis: 'L',
  status: 'Borrador',
  notes: '',
  tech_params: [],
  lines: [emptyFormulaLine('kg')],
});

export const emptyBatch = () => ({
  id: null,
  code: '',
  formula_id: '',
  formula_code: '',
  request_code: '',
  product_code: '',
  product_name: '',
  color_code: '',
  formula_kind: FORMULA_KIND_BASE,
  planned_qty: 100,
  actual_qty: 0,
  unit: 'L',
  format: 'Tineta 16 L',
  status: 'Borrador',
  process_stage: 'planning',
  planned_date: todayIso(),
  warehouse: WAREHOUSE_OPTIONS[0],
  responsible: RESPONSIBLE_OPTIONS[0],
  notes: '',
  events: [],
});

export const inventoryStagesFromBatches = (batches = []) => {
  const qty = (statuses) =>
    batches
      .filter((row) => statuses.includes(row.status))
      .reduce((acc, row) => acc + Number(row.actual_qty || row.planned_qty || 0), 0);

  return [
    {
      key: 'mp',
      label: 'Materia prima',
      account: '1.2.02',
      hint: 'Stock de adquisiciones disponible para emitir lotes.',
      batches: 0,
      qty: MATERIAL_CATALOG.filter((row) => row.kind === 'Materias primas').reduce((acc, row) => acc + row.stock, 0),
      unit: 'u. físicas',
    },
    {
      key: 'wip',
      label: 'Producto en proceso',
      account: '1.2.05',
      hint: 'Lotes emitidos o en molienda / mezclado.',
      batches: batches.filter((row) => ['Emitida', 'En proceso', 'Reproceso'].includes(row.status)).length,
      qty: qty(['Emitida', 'En proceso', 'Reproceso']),
      unit: 'L',
    },
    {
      key: 'manufactured',
      label: 'Producto fabricado',
      account: '1.2.06',
      hint: 'Fabricado, pendiente o en control de calidad.',
      batches: batches.filter((row) => ['Fabricado', 'En calidad'].includes(row.status)).length,
      qty: qty(['Fabricado', 'En calidad']),
      unit: 'L',
    },
    {
      key: 'pt',
      label: 'Producto terminado',
      account: '1.2.07',
      hint: 'Liberado a despacho (producción.finished).',
      batches: batches.filter((row) => row.status === 'Terminado').length,
      qty: qty(['Terminado']),
      unit: 'L',
    },
  ];
};
